import type { LinkedDocumentationItem } from 'shared/domain/issue/linkedDocumentation';
import { IssueStageReference } from 'shared/domain/issue/stageReferences';
import { Identificable, LabelledEntity } from 'shared/types/commonView';
import { DocumentOutDto } from './document';
import { EventDescriptionType } from 'shared/types/eventDescription';
interface EstimatedCost {
  cost: string;
  coveredBy: string | undefined;
}

interface FinalCost {
  outstanding: string;
  settled: string;
  coveredBy: string | undefined;
}

interface EventDescriptionPartOutDto {
  type: EventDescriptionType;
  text?: string;
  user?: string;
}

export type IssueEventOutDto = {
  title: string;
  type: string;
  description: EventDescriptionPartOutDto[];
  deleted: boolean;
  documents: DocumentOutDto[];
} & Identificable;

export type LinkedDocumentationOutDto = {
  _id?: string;
  documentationId?: string;
  versionId?: string;
  sourceDocumentId?: string;
  createdAt?: string;
  createdBy?: string;
  name?: string;
  description?: string;
  documentationName?: string;
  label?: string;
  imageRotation?: number;
};

export function linkedDocumentationItemsToOutDtos(
  items: LinkedDocumentationItem[]
): LinkedDocumentationOutDto[] {
  return items.map((item) => ({
    _id: item._id,
    documentationId: item.documentationId,
    versionId: item.versionId,
    sourceDocumentId: item.sourceDocumentId,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
    name: item.name,
    description: item.description,
    documentationName: item.documentationName,
    label: item.label,
    imageRotation: item.imageRotation,
  }));
}

interface Point { x: number; y: number }
type Area = Point[];

export type IssueOutDto = {
  primaryData: {
    site: string;
    title: string;
    assignee: Identificable;
    description: string;
    detectionDate: string;
    subcontractors: string[];
    contractNumbers: string;
    level: string;
    positionOnMap: any;
    targetAreas: Area[];
    finalAreas: Area[];
  };
  extendedData: {
    workTypes?: string[];
    rootCauses?: string[];
    estimatedCost?: EstimatedCost[];
    finalCost?: FinalCost[];
    targetCompletionDate?: string;
    finalCompletionDate?: string;
    targetResponseToAuthorDate?: string;
    finalResponseToAuthorDate?: string;
    solutionProposal?: string;
    impact?: string | undefined;
    environmentalAspect?: string | undefined;
    effect?: string | undefined;
    hazardCategory?: string[] | undefined;
    expectedFine?: number;
    imposedFine?: number;
    solutionMethod?: string;
    subcontractorRepresentative?: string;
    decisionToImposeFine?: string;
    circumstances?: string;
    issueVerification?: string;
    delayReason?: string;
    rejectionReason?: string;
    proposedCorrectiveAction?: string;
    costCode?: string;
    daysOfInabilityToWork?: number;
    personUnableToWork?: string;
    spilledSubstance?: string;
    waterLeakageScale?: number;
    soilLeakageScale?: number;
    contaminatedSoilScale?: number;
    targetStartDate?: string;
    finalStartDate?: string;
    completionDateDelay?: string;
    startDateDelay?: string;
    responseToAuthorDelay?: string;
    executedByCompanies?: string[];
    targetAmount?: number;
    completedAmount?: number;
    amountPercentage?: number;
    numberOfEmployees?: number;
    priority?: string | undefined;
    projectStage?: string | undefined;
    sickLeaveStartDate?: string;
    sickLeaveEndDate?: string;
    supervisor?: string;
    victim?: string;
    victimJobTitle?: string;
    employeeNumber?: string;
    employmentType?: string;
    immediateMeasures?: string;
    victimActivity?: string;
    injuryTypes?: string[];
    injuredBodyParts?: string[];
    applicationNumber?: string;
    productType?: string;
    producerDistributor?: string;
    typeModel?: string;
    installationLocation?: string;
    requirements?: string[];
    installationBasis?: string[];
    constructionProductDocuments?: string[];
    deviceDocuments?: string[];
    additionalDocuments?: string[];
  };
  inspection?: string;
  protocolItem?: string;
  process: string;
  deleted: boolean;
  events: IssueEventOutDto[];
  mainImage?: string | null;
  linkedDocumentation?: LinkedDocumentationOutDto[];
} & Identificable &
  IssueStageReference;

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type UpdateIssueOutDto = DeepPartial<IssueOutDto>;
